import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRoot, baseQueryWithRetryAndReAuth } from "../../global";

export const api = createApi({
  reducerPath: "runTheDayApi",
  baseQuery: baseQueryWithRetryAndReAuth,
  tagTypes: [
    "Login",
    "UserManagement",
    "Race",
    "RaceDetails",
    "Reports",
    "Seo",
    "ServerInstances",
    "Transactions",
    "Chat"
  ],
  endpoints: (builder) => ({
    rtdLogging: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["Login"],
    }),

    verifyOtp: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["Login"],
    }),

    changePassword: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["Login"],
    }),

    resendOtp: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["Login"],
    }),

    logOut: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
      }),
      invalidatesTags: ["Login"],
    }),

    getSubAdminList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-subadmins?limit=${data.limit}&offset=${data.offset}&searchtext=${data.searchtext}&sort=${data.sort}`,
        method: "GET",
      }),
      providesTags: ["UserManagement"],
    }),

    getRunnerList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-users/runners?limit=${data.limit}&offset=${data.offset}&searchtext=${data.searchtext}&sort=${data.sort}`,
        method: "GET",
      }),
      providesTags: ["UserManagement"],
    }),

    getRaceDirectorList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-users/race-directors?limit=${data.limit}&offset=${data.offset}&searchtext=${data.searchtext}&sort=${data.sort}`,
        method: "GET",
      }),
      providesTags: ["UserManagement"],
    }),


    getTimerList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-users/timers?limit=${data.limit}&offset=${data.offset}&searchtext=${data.searchtext}&sort=${data.sort}`,
        method: "GET",
      }),
      providesTags: ["UserManagement"],
    }),

    getTimerRacesList: builder.query<any, any>({
      query: (id: string) => ({
        url: `${apiRoot}timer/${id}/races`,
        method: "GET",
      }),
      providesTags: ["UserManagement"],
    }),

    removeRaceFromTimer: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}timer/remove-race`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement", "RaceDetails"],
    }),

    reGeneratePassword: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    changeUserRole: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    changeRunscoreCredentials: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    addSubAdmin: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    updateSubAdmin: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    updateStatus: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement", "RaceDetails"],
    }),


    addTimer: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    updateTimer: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    getRaceList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}races?status=${data.status}&limit=${data.limit}&offset=${data.offset}&completionstatus=${data.completionstatus}&searchtext=${data.searchtext}&sort=${data.sort}`,
        method: "GET",
      }),
      providesTags: ["Race"],
    }),

    getRaceDetails: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data}`,
        method: "GET",
      }),
      providesTags: ["Race"],
    }),

    getFeaturedRace: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race-featured-requests?limit=${data.limit}&offset=${data.offset}`,
        method: "GET",
        body: data.body,
      }),
      providesTags: ["Race"],
    }),

    updateFeaturedRace: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["Race"],
    }),

    getRaceDetailsSponsors: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data}/sponsors`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    getRaceDetailsCoupons: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data.id}/coupons?limit=${data.limit}&searchtext=${data.searchtext || ""
          }&offset=${data.offset}`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    getRaceDetailsTeams: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data.id}/teams?searchtext=${data.searchtext}&limit=${data.limit}&offset=${data.offset}`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    getRaceDetailsTimer: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}timer/by-race/${data.id}?searchtext=${data.searchtext}&limit=${data.limit}&offset=${data.offset}`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    getTeamDetails: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}team-details/${data}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getRgistrationTypeList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-registration-types/${data.id}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getEventsTypeList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}events-by-registration-type/${data.id}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getTeamsTypeList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}teams-by-race-id/${data.id}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getTShirtList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-tshirts-dropdown?race_id=${data.id}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getDefaultValueCSV: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-defualt-values-for-csv?race_id=${data.id}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getRaceDetailsDonations: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data.id}/donations?searchtext=${data.searchtext}&limit=${data.limit}&offset=${data.offset}`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    getLiveResultStatus: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}trigger-live-result?live_result_status=${data.status}&race_id=${data.id}`,
        method: "GET",
      }),
      invalidatesTags: ["RaceDetails", "Race"],
    }),

    getParticipantDetails: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}participant/${data}/detail`,
        method: "GET",
      }),
    }),

    updateRaceDetailStatus: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails", "Race"],
    }),

    getParticipantsListCsvDownload: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      providesTags: ["RaceDetails"],
    }),

    getParticipantsListCsv: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      providesTags: ["RaceDetails"],
    }),

    updateParticipantsList: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    addManuallyParticipants: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    addManuallyDonations: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    getRaceCategoriesList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data.id}/categories`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    getMasterGenderList: builder.query<any, any>({
      query: () => ({
        url: `${apiRoot}get-master-data`,
        method: "GET",
      }),
    }),

    addCategories: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    updateRaceCategories: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    updateTeamGoal: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PATCH",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),


    getAllTimersList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-users/timers`,
        method: "GET",
      }),
      providesTags: ["RaceDetails"],
    }),

    assignTimer: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    unAssignTimer: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    deleteRaceCategories: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "DELETE",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    addWebsiteUrl: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    getRegisterReportList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}reports/${data.id}/registration?search-text=${data.searchText || ""
          }&limit=${data.limits}&offset=${data.offsets}&sort=${data.sort}`,
        body: data.body,
        method: "GET",
      }),
      providesTags: ["Reports"],
    }),

    getDonationReportList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}reports/${data.id}/donation?search-text=${data.searchText || ""}&limit=${data.limits
          }&offset=${data.offsets}&sort=${data.sort}`,
        body: data.body,
        method: "GET",
      }),
      providesTags: ["Reports"],
    }),

    getVolunteerReportList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}reports/${data.id}/volunteer?search-text=${data.searchText || ""}&limits=${data.limits
          }&offset=${data.offsets}&sort=${data.sort}`,
        body: data.body,
        method: "GET",
      }),
      providesTags: ["Reports"],
    }),

    getTshirtsReportList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}reports/${data.id}/tshirt?search-text=${data.searchText || ""}&limit=${data.limit
          }&offset=${data.offsets}&sort=${data.sort}`,
        body: data.body,
        method: "GET",
      }),
      providesTags: ["Reports"],
    }),

    getFundRaisingReportsList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}reports/${data.id}/individualfundraising?limit=${data.limits}&offset=${data.offsets
          }&search-text=${data.searchText || ""}`,
        body: data.body,
        method: "GET",
      }),
      providesTags: ["Reports"],
    }),

    getAddOnReportsList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}reports/${data.id}/addonproducts?limit=${data.limits}&offset=${data.offsets
          }&search-text=${data.searchText || ""}`,
        body: data.body,
        method: "GET",
      }),
      providesTags: ["Reports"],
    }),

    getImpersonateToken: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
    }),

    getRefreshToken: builder.query<any, any>({
      query: () => ({
        url: `${apiRoot}get-new-accesstoken`,
        method: "GET",
      }),
      providesTags: ["Login"],
    }),

    getFeaturedRaceList: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race-featured-requests?limit=${data.limit}&offset=${data.offset}&type=${data.type}`,
        method: "GET",
        body: data.body,
      }),
      providesTags: ["Race"],
    }),

    getCloneRace: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data.id}/clone-race?title=${data.title}`,
        method: "GET",
      }),
      invalidatesTags: ["Race"],
    }),

    getDeleteRace: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}delete-race/${data}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Race"],
    }),

    getDeleteParticipant: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}participant/${data}`,
        method: "DELETE",
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    getCompleteManualReset: builder.mutation<any, any>({
      query: (id: string) => ({
        url: `${apiRoot}race/${id}/complete-reset`,
        method: "GET",
      }),
      invalidatesTags: ["Race", "RaceDetails"],
    }),

    deleteRaceDonation: builder.mutation<any, any>({
      query: (id: string) => ({
        url: `${apiRoot}delete-donation/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["RaceDetails"],
    }),

    getDirectorsListing: builder.query<any, any>({
      query: ({ stage, limit, offset, searchText }: any) => ({
        url: `${apiRoot}rd-details?stage=${stage}&limit=${limit}&offset=${offset}&searchText=${searchText || ""
          }`,
        method: "GET",
      }),
    }),

    getDirectorsCount: builder.query<any, any>({
      query: (id: string) => ({
        url: `${apiRoot}rd-count-step-wise`,
        method: "GET",
      }),
    }),

    getRegistrationTypeDetails: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}get-registration-types/${data.id}`,
        method: "GET",
      }),
      providesTags: [],
    }),

    getUnregisteredRdDetails: builder.mutation<any, any>({
      query: (stage: string) => ({
        url: `${apiRoot}unregistered-user-details?stage=${stage}`,
        method: "GET",
      }),
    }),

    addRaceDirector: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["UserManagement"],
    }),

    handleSmartTiming: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "PUT",
        body: data.body,
      }),
      invalidatesTags: ["RaceDetails", "Race"],
    }),

    handleDeleteVolunteer: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reports"],
    }),

    updateRaceDetailSeo: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}${data.url}`,
        method: "POST",
        body: data.body,
      }),
      invalidatesTags: ["Seo"],
    }),

    getRaceDetailsSeo: builder.query<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race-seo/get-race-seo/${data}`,
        method: "GET",
      }),
      providesTags: ["Seo"],
    }),

    getClearCache: builder.query<any, any>({
      query: (id: string) => ({
        url: `${apiRoot}race/${id}/flush-cache`,
        method: "GET",
      }),
    }),

    updateSenderName: builder.mutation<any, any>({
      query: (data: any) => ({
        url: `${apiRoot}race/${data.raceId}/sender-name`,
        method: "POST",
        body: data?.body,
      }),
      invalidatesTags: ["Race"],
    }),

    getAuditLogs: builder.query<any, any>({
      query: (params: any) => {
        let url = `${apiRoot}audit-logs?limit=${params.limit}&offset=${params.offset}`;
        if (params.searchtext) url += `&session_id=${encodeURIComponent(params.searchtext)}`;
        if (params.startDate) url += `&start_date=${params.startDate}`;
        if (params.endDate) url += `&end_date=${params.endDate}`;
        if (params.user) url += `&user=${encodeURIComponent(params.user)}`;
        if (params.module) url += `&module=${encodeURIComponent(params.module)}`;
        if (params.actionType) url += `&actionType=${encodeURIComponent(params.actionType)}`;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: ["Reports"], // Suggesting Reports tag as it's an audit log
    }),

    // ─── Server Management Endpoints ───
    getServerInstances: builder.query<any, void>({
      query: () => ({
        url: `${apiRoot}server/instances`,
        method: "GET",
      }),
      providesTags: ["ServerInstances"],
    }),

    serverPowerAction: builder.mutation<any, { server_key: string; action: "START" | "STOP" }>({
      query: (data) => ({
        url: `${apiRoot}server/power`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ServerInstances"],
    }),

    getServerStatus: builder.query<any, string>({
      query: (server_key) => ({
        url: `${apiRoot}server/status/${server_key}`,
        method: "GET",
      }),
    }),

    // ─── Transactions Dashboard Endpoints ───
    getTransactionStats: builder.query<any, any>({
      query: (params: any) => {
        let url = `${apiRoot}dashboard/transaction-stats`;
        const qp: string[] = [];
        if (params?.module) qp.push(`module=${encodeURIComponent(params.module)}`);
        if (params?.race_id) qp.push(`race_id=${encodeURIComponent(params.race_id)}`);
        if (params?.date_from) qp.push(`date_from=${encodeURIComponent(params.date_from)}`);
        if (params?.date_to) qp.push(`date_to=${encodeURIComponent(params.date_to)}`);
        if (qp.length) url += `?${qp.join("&")}`;
        return { url, method: "GET" };
      },
      providesTags: ["Transactions"],
    }),

    getTransactions: builder.query<any, any>({
      query: (params: any) => {
        let url = `${apiRoot}dashboard/transactions`;
        const qp: string[] = [];
        if (params?.module) qp.push(`module=${encodeURIComponent(params.module)}`);
        if (params?.race_id) qp.push(`race_id=${encodeURIComponent(params.race_id)}`);
        if (params?.date_from) qp.push(`date_from=${encodeURIComponent(params.date_from)}`);
        if (params?.date_to) qp.push(`date_to=${encodeURIComponent(params.date_to)}`);
        if (params?.status) qp.push(`status=${encodeURIComponent(params.status)}`);
        qp.push(`limit=${params?.limit || 10}`);
        if (params?.cursor) qp.push(`cursor=${encodeURIComponent(params.cursor)}`);
        if (qp.length) url += `?${qp.join("&")}`;
        return { url, method: "GET" };
      },
      providesTags: ["Transactions"],
    }),

    manualRetargetEmail: builder.mutation<any, { multipleRegistrationIds: string[]; filters?: any }>({
      query: (data) => ({
        url: `${apiRoot}dashboard/retarget-email`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Transactions"],
    }),

    getRetargetAnalytics: builder.query<any, any>({
      query: (params: any) => {
        let url = `${apiRoot}dashboard/retarget-analytics`;
        const qp: string[] = [];
        if (params?.race_id) qp.push(`race_id=${encodeURIComponent(params.race_id)}`);
        if (params?.date_from) qp.push(`date_from=${encodeURIComponent(params.date_from)}`);
        if (params?.date_to) qp.push(`date_to=${encodeURIComponent(params.date_to)}`);
        if (qp.length) url += `?${qp.join("&")}`;
        return { url, method: "GET" };
      },
      providesTags: ["Transactions"],
    }),

    getRacesDropdown: builder.query<any, any>({
      query: (params: any) => {
        let url = `${apiRoot}races?status=PUBLISHED&completionstatus=ALL&sort=-1`;
        if (params?.limit) url += `&limit=${params.limit}`;
        else url += `&limit=20`;
        if (params?.offset !== undefined) url += `&offset=${params.offset}`;
        else url += `&offset=0`;
        if (params?.searchtext) url += `&searchtext=${encodeURIComponent(params.searchtext)}`;
        else url += `&searchtext=`;
        return { url, method: "GET" };
      },
    }),

    getChatThreads: builder.query<any, { limit?: number; offset?: number; startDate?: string; endDate?: string; status?: string; search?: string } | void>({
      query: (params) => {
        let url = `${apiRoot}chat/threads`;
        const qp: string[] = [];
        if (params) {
          if (params.limit !== undefined) qp.push(`limit=${params.limit}`);
          if (params.offset !== undefined) qp.push(`offset=${params.offset}`);
          if (params.startDate) qp.push(`startDate=${encodeURIComponent(params.startDate)}`);
          if (params.endDate) qp.push(`endDate=${encodeURIComponent(params.endDate)}`);
          if (params.status) qp.push(`status=${encodeURIComponent(params.status)}`);
          if (params.search) qp.push(`search=${encodeURIComponent(params.search)}`);
        }
        if (qp.length) url += `?${qp.join("&")}`;
        return { url, method: "GET" };
      },
      providesTags: ["Chat"],
    }),


    getChatThreadDetails: builder.query<any, string>({
      query: (conversationId) => ({
        url: `${apiRoot}chat/threads/${conversationId}`,
        method: "GET",
      }),
      providesTags: ["Chat"],
    }),

    getChatSummary: builder.query<any, { startDate?: string; endDate?: string } | void>({
      query: (params) => {
        let url = `${apiRoot}chat/summary`;
        const qp: string[] = [];
        if (params?.startDate) qp.push(`startDate=${encodeURIComponent(params.startDate)}`);
        if (params?.endDate) qp.push(`endDate=${encodeURIComponent(params.endDate)}`);
        if (qp.length) url += `?${qp.join("&")}`;
        return { url, method: "GET" };
      },
      providesTags: ["Chat"],
    }),

    updateChatStatus: builder.mutation<any, { conversationId: string; status: string }>({
      query: ({ conversationId, status }) => ({
        url: `${apiRoot}chat/threads/${conversationId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Chat"],
    }),
  }),
});

export const {
  useRtdLoggingMutation,
  useVerifyOtpMutation,
  useChangePasswordMutation,
  useGetSubAdminListQuery,
  useGetRunnerListQuery,
  useGetRaceDirectorListQuery,
  useGetTimerListQuery,
  useGetTimerRacesListQuery,
  useRemoveRaceFromTimerMutation,
  useReGeneratePasswordMutation,
  useChangeUserRoleMutation,
  useChangeRunscoreCredentialsMutation,
  useAddSubAdminMutation,
  useUpdateSubAdminMutation,
  useUpdateStatusMutation,
  useAddTimerMutation,
  useUpdateTimerMutation,
  useResendOtpMutation,
  useLogOutMutation,
  useGetRaceListQuery,
  useGetFeaturedRaceQuery,
  useUpdateFeaturedRaceMutation,
  useGetRaceDetailsQuery,
  useGetRaceDetailsSponsorsQuery,
  useGetRaceDetailsCouponsQuery,
  useGetRaceDetailsTeamsQuery,
  useGetRaceDetailsTimerQuery,
  useGetTeamDetailsQuery,
  useGetRaceDetailsDonationsQuery,
  useUpdateRaceDetailStatusMutation,
  useGetLiveResultStatusMutation,
  useGetParticipantDetailsMutation,
  useGetParticipantsListCsvDownloadQuery,
  useUpdateParticipantsListMutation,
  useAddManuallyParticipantsMutation,
  useAddManuallyDonationsMutation,
  useGetParticipantsListCsvQuery,
  useGetRaceCategoriesListQuery,
  useGetRgistrationTypeListQuery,
  useGetEventsTypeListQuery,
  useGetTeamsTypeListQuery,
  useGetTShirtListQuery,
  useGetDefaultValueCSVQuery,
  useGetMasterGenderListQuery,
  useAddCategoriesMutation,
  useUpdateRaceCategoriesMutation,
  useUpdateTeamGoalMutation,
  useGetAllTimersListQuery,
  useAssignTimerMutation,
  useUnAssignTimerMutation,
  useAddWebsiteUrlMutation,
  useDeleteRaceCategoriesMutation,
  useGetRegisterReportListQuery,
  useGetDonationReportListQuery,
  useGetVolunteerReportListQuery,
  useGetTshirtsReportListQuery,
  useGetFundRaisingReportsListQuery,
  useGetAddOnReportsListQuery,
  useGetImpersonateTokenMutation,
  useGetRefreshTokenQuery,
  useGetFeaturedRaceListQuery,
  useGetCloneRaceMutation,
  useGetDeleteRaceMutation,
  useGetDeleteParticipantMutation,
  useGetCompleteManualResetMutation,
  useDeleteRaceDonationMutation,
  useGetDirectorsCountQuery,
  useGetDirectorsListingQuery,
  useGetRegistrationTypeDetailsQuery,
  useAddRaceDirectorMutation,
  useGetUnregisteredRdDetailsMutation,
  useHandleSmartTimingMutation,
  useHandleDeleteVolunteerMutation,
  useUpdateRaceDetailSeoMutation,
  useGetRaceDetailsSeoQuery,
  useLazyGetClearCacheQuery,
  useUpdateSenderNameMutation,
  useGetAuditLogsQuery,
  useGetServerInstancesQuery,
  useServerPowerActionMutation,
  useLazyGetServerStatusQuery,
  useLazyGetTransactionStatsQuery,
  useLazyGetTransactionsQuery,
  useManualRetargetEmailMutation,
  useGetRetargetAnalyticsQuery,
  useLazyGetRetargetAnalyticsQuery,
  useGetRacesDropdownQuery,
  useLazyGetRacesDropdownQuery,
  useGetChatThreadsQuery,
  useGetChatThreadDetailsQuery,
  useGetChatSummaryQuery,
  useUpdateChatStatusMutation,
} = api;
